import { Link } from 'react-router-dom'
import { Logo } from '@/components/Logo'
import { getMobileDownloadLinks, hasPublishedStoreLinks } from '@/lib/mobile-download'

function StoreButton({
  href,
  label,
  sublabel,
}: {
  href: string
  label: string
  sublabel: string
}) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="flex min-w-[220px] flex-col rounded-none border border-ovation-ink bg-ovation-ink px-6 py-4 text-ovation-paper transition-colors hover:bg-ovation-ink/85"
    >
      <span className="text-xs uppercase tracking-[0.18em] text-ovation-paper/70">{sublabel}</span>
      <span className="mt-1 font-display text-2xl">{label}</span>
    </a>
  )
}

export function DownloadPage() {
  const links = getMobileDownloadLinks()
  const published = hasPublishedStoreLinks(links)

  return (
    <div className="min-h-screen bg-ovation-paper">
      <header className="border-b border-ovation-ink/8">
        <div className="mx-auto flex max-w-5xl items-center justify-between px-6 py-5">
          <Link to="/" className="flex items-center gap-3" aria-label="Ovation home">
            <Logo showWordmark={false} />
            <span className="font-display text-2xl text-ovation-ink">Ovation</span>
          </Link>
          <Link to="/login" className="text-sm text-ovation-ink/70 hover:text-ovation-ink">
            Log in
          </Link>
        </div>
      </header>

      <main className="mx-auto max-w-5xl px-6 py-16">
        <p className="text-xs uppercase tracking-[0.22em] text-ovation-accent font-medium">
          Mobile apps
        </p>
        <h1 className="mt-4 max-w-2xl font-display text-5xl leading-tight text-ovation-ink">
          Ovation in your pocket — for managers and crew on the move.
        </h1>
        <p className="mt-6 max-w-2xl text-sm leading-relaxed text-ovation-muted">
          Download the Ovation app to check pipeline updates, review reports, manage your crew CV,
          and keep your linked diary synced across every artist you work with. The app connects to
          your existing Ovation account — no separate data store.
        </p>

        <div className="mt-10 grid gap-8 lg:grid-cols-2">
          <section className="border border-ovation-ink/12 p-8">
            <h2 className="font-display text-3xl text-ovation-ink">For management</h2>
            <ul className="mt-4 space-y-2 text-sm text-ovation-muted">
              <li>Dashboard and pipeline at a glance</li>
              <li>Inbox updates and triage alerts</li>
              <li>Quarterly reports and deal bands</li>
              <li>Switch between artists in your roster</li>
            </ul>
          </section>

          <section className="border border-ovation-ink/12 bg-ovation-ink p-8 text-ovation-paper">
            <h2 className="font-display text-3xl">For crew</h2>
            <ul className="mt-4 space-y-2 text-sm text-ovation-paper/75">
              <li>Portable CV visible to Ovation artists</li>
              <li>Linked diary across multiple artists</li>
              <li>Gig offers, interview requests, and availability on tour</li>
              <li>Push alerts when you are offered a role or shortlisted</li>
            </ul>
          </section>
        </div>

        <div className="mt-12">
          <h2 className="font-display text-2xl text-ovation-ink">Download</h2>

          {published ? (
            <div className="mt-6 flex flex-wrap gap-4">
              {links.iosAppStoreUrl ? (
                <StoreButton
                  href={links.iosAppStoreUrl}
                  sublabel="Download on the"
                  label="App Store"
                />
              ) : null}
              {links.androidPlayStoreUrl ? (
                <StoreButton
                  href={links.androidPlayStoreUrl}
                  sublabel="Get it on"
                  label="Google Play"
                />
              ) : null}
              {links.androidApkUrl ? (
                <a
                  href={links.androidApkUrl}
                  className="flex min-w-[220px] flex-col rounded-none border border-ovation-ink px-6 py-4 text-ovation-ink transition-colors hover:bg-ovation-ink/5"
                >
                  <span className="text-xs uppercase tracking-[0.18em] text-ovation-muted">
                    Direct download
                  </span>
                  <span className="mt-1 font-display text-2xl">Android APK</span>
                </a>
              ) : null}
            </div>
          ) : (
            <div className="mt-6 max-w-xl border border-ovation-ink/12 bg-white p-6">
              <p className="text-sm leading-relaxed text-ovation-muted">
                Store listings are being prepared. Join the platform now on web — when the iOS and
                Android apps launch, sign in with the same account and your data will already be
                there.
              </p>
              <div className="mt-6 flex flex-wrap gap-3">
                <Link
                  to="/join/artist"
                  className="inline-flex border border-ovation-ink bg-ovation-ink px-5 py-3 text-sm font-medium text-ovation-paper hover:bg-ovation-ink/85"
                >
                  Get started as management
                </Link>
                <Link
                  to="/join"
                  className="inline-flex border border-ovation-ink px-5 py-3 text-sm font-medium text-ovation-ink hover:bg-ovation-ink/5"
                >
                  Create crew profile
                </Link>
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  )
}
